// src/lib/storeProducts.js
// Store-specific product management system

import { db } from './firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  startAfter, 
  doc, 
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { STORE_TYPES } from './storeManagement';

const PAGE_SIZE = 24;

/**
 * Get the collection name for a specific store
 */
export function getStoreProductsCollection(storeType, storeId = null) {
  switch (storeType) {
    case STORE_TYPES.COOPERATIVE:
      return storeId ? `cooperative_products_${storeId}` : 'cooperative_products';
    case STORE_TYPES.MEMBER:
      return `member_products_${storeId}`;
    case STORE_TYPES.ESTABLISHMENT:
      return `establishment_products_${storeId}`;
    default:
      throw new Error(`Unknown store type: ${storeType}`);
  }
}

/**
 * Fetch products for a specific store
 */
export async function fetchStoreProductsPage({ 
  storeType, 
  storeId = null, 
  category = "", 
  after = null, 
  pageSize,
  query: searchQuery = ""
} = {}) {
  if (!storeType) {
    throw new Error('storeType is required');
  }

  const collectionName = getStoreProductsCollection(storeType, storeId);
  
  // Base query with active filter
  const base = [
    collection(db, collectionName), 
    where("active", "==", true)
  ];

  // Add category filter if specified
  if (category) {
    base.push(where("categories", "array-contains", category));
  }

  // Note: Search query filtering is handled in the client-side filtering
  // since the searchable field might not exist on all products

  // For search queries, fetch more items since we're doing client-side filtering
  const size = searchQuery 
    ? Math.max(1, Math.min(500, pageSize || 200)) // Fetch more for search
    : Math.max(1, Math.min(100, pageSize || PAGE_SIZE)); // Normal pagination
  let q = query(
    ...base,
    orderBy("createdAt", "desc"),
    limit(size)
  );
  
  if (after) {
    q = query(q, startAfter(after));
  }

  async function run(qry) {
    const snap = await getDocs(qry);
    let items = snap.docs.map((d) => ({ 
      id: d.id, 
      ...d.data(), 
      _doc: d,
      storeType,
      storeId
    }))
      // Soft delete safeguard
      .filter((x) => x.deleted !== true);
    
    // Apply client-side search filtering if search query is provided
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      
      // Create search synonyms for common Filipino terms
      const searchSynonyms = {
        'sibuyas': ['onion', 'onions', 'yellow onion', 'red onion', 'white onion'],
        'bawang': ['garlic', 'garlic cloves'],
        'luya': ['ginger', 'ginger root'],
        'kamatis': ['tomato', 'tomatoes'],
        'talong': ['eggplant', 'aubergine'],
        'okra': ['okra', 'lady finger'],
        'ampalaya': ['bitter gourd', 'bitter melon'],
        'sitaw': ['string beans', 'green beans'],
        'patola': ['sponge gourd', 'luffa'],
        'kalabasa': ['squash', 'pumpkin'],
        'repolyo': ['cabbage'],
        'carrots': ['carrot', 'carrots'],
        'potato': ['potatoes', 'potato'],
        'rice': ['bigas', 'kanin'],
        'fish': ['isda', 'fish'],
        'meat': ['karne', 'meat'],
        'chicken': ['manok', 'chicken'],
        'pork': ['baboy', 'pork'],
        'beef': ['baka', 'beef'],
        'lettuce': ['lettuce', 'lett', 'salad', 'green leafy'],
        'peanut': ['peanut', 'mani', 'groundnut', 'pean'],
        'groundnut': ['groundnut', 'peanut', 'mani', 'pean'],
        'mani': ['mani', 'peanut', 'groundnut', 'pean']
      };
      
      // Get all possible search terms including synonyms
      const allSearchTerms = [searchLower];
      if (searchSynonyms[searchLower]) {
        allSearchTerms.push(...searchSynonyms[searchLower]);
      }
      
      items = items.filter(product => {
        const productText = [
          product.name,
          product.sku,
          product.description,
          product.searchable,
          ...(product.categories || [])
        ].join(' ').toLowerCase();
        
        // Check if any search term matches
        return allSearchTerms.some(term => productText.includes(term));
      });
      
    }
    
    const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { items, nextCursor };
  }

  try {
    return await run(q);
  } catch (e) {
    // Likely missing composite index (failed-precondition)
    if (e && e.code === "failed-precondition") {
      try {
        let q2 = query(...base, orderBy("name"), limit(size));
        if (after) q2 = query(q2, startAfter(after));
        return await run(q2);
      } catch (e2) {
        // As a last resort, drop ordering and client-sort
        let q3 = query(...base, limit(size));
        if (after) q3 = query(q3, startAfter(after));
        const res = await run(q3);
        const items = [...res.items].sort((a, b) => {
          const ca = a.createdAt?.seconds ?? a.createdAt?._seconds ?? null;
          const cb = b.createdAt?.seconds ?? b.createdAt?._seconds ?? null;
          if (ca && cb) return cb - ca;
          if (ca && !cb) return -1;
          if (!ca && cb) return 1;
          const na = (a.name || "").toLowerCase();
          const nb = (b.name || "").toLowerCase();
          return na.localeCompare(nb);
        });
        return { items, nextCursor: res.nextCursor };
      }
    }
    throw e;
  }
}

/**
 * Get a single product by ID from a specific store
 */
export async function getStoreProductById(storeType, storeId, productId) {
  const collectionName = getStoreProductsCollection(storeType, storeId);
  const ref = doc(db, collectionName, productId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = { id: snap.id, ...snap.data() };
  if (data.deleted === true) return null;
  if (data.active === false) return null;
  return { ...data, storeType, storeId };
}

/**
 * Add a product to a specific store
 */
export async function addStoreProduct(storeType, storeId, productData) {
  console.log("🔍 DEBUG: addStoreProduct called with:");
  console.log("  - storeType:", storeType);
  console.log("  - storeId:", storeId);
  console.log("  - productData:", productData);
  
  try {
    const collectionName = getStoreProductsCollection(storeType, storeId);
    console.log("🔍 DEBUG: Collection name:", collectionName);
    
    const payload = {
      ...productData,
      storeType,
      storeId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    console.log("🔍 DEBUG: Final payload for Firestore:", payload);
    
    // DEBUG: Check if we have access to the database
    console.log("🔍 DEBUG: Database instance:", db);
    console.log("🔍 DEBUG: Database app:", db.app);
    
    // DEBUG: Try to access the collection first
    const collectionRef = collection(db, collectionName);
    console.log("🔍 DEBUG: Collection reference:", collectionRef);
    console.log("🔍 DEBUG: Collection path:", collectionRef.path);
    
    console.log("🔍 DEBUG: Attempting to add document...");
    const docRef = await addDoc(collectionRef, payload);
    console.log("✅ DEBUG: Document added successfully with ID:", docRef.id);
    
    const result = { id: docRef.id, ...payload };
    console.log("✅ DEBUG: Returning result:", result);
    return result;
    
  } catch (error) {
    console.error("❌ DEBUG: Error in addStoreProduct:");
    console.error("  - Error:", error);
    console.error("  - Error name:", error.name);
    console.error("  - Error message:", error.message);
    console.error("  - Error code:", error.code);
    console.error("  - Error stack:", error.stack);
    
    // Additional debugging for common issues
    if (error.code === 'permission-denied') {
      console.error("❌ DEBUG: PERMISSION DENIED DETAILS:");
      console.error("  - Collection:", getStoreProductsCollection(storeType, storeId));
      console.error("  - Store Type:", storeType);
      console.error("  - Store ID:", storeId);
      console.error("  - This usually means Firestore security rules are blocking the write");
    }
    
    if (error.code === 'invalid-argument') {
      console.error("❌ DEBUG: INVALID ARGUMENT DETAILS:");
      console.error("  - Payload keys:", Object.keys(payload));
      console.error("  - Payload values:", Object.values(payload));
      console.error("  - This usually means the data doesn't match Firestore schema");
    }
    
    throw error; // Re-throw the error so it can be caught by the calling function
  }
}

/**
 * Update a product in a specific store
 */
export async function updateStoreProduct(storeType, storeId, productId, updateData) {
  const collectionName = getStoreProductsCollection(storeType, storeId);
  const ref = doc(db, collectionName, productId);
  
  const payload = {
    ...updateData,
    updatedAt: serverTimestamp()
  };

  await updateDoc(ref, payload);
  return { id: productId, ...payload };
}

/**
 * Delete a product from a specific store
 */
export async function deleteStoreProduct(storeType, storeId, productId) {
  const collectionName = getStoreProductsCollection(storeType, storeId);
  const ref = doc(db, collectionName, productId);
  await deleteDoc(ref);
  return true;
}

/**
 * Get all products for a store (for admin/management purposes)
 */
export async function getAllStoreProducts(storeType, storeId) {
  const collectionName = getStoreProductsCollection(storeType, storeId);
  const q = query(collection(db, collectionName), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  
  return snap.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    storeType,
    storeId
  }));
}

/**
 * Find a product by slug across all store collections
 */
export async function findProductBySlug(slug) {
  if (!slug) return null;

  try {
    // Search cooperative products (generic)
    const cooperativeQuery = query(
      collection(db, 'cooperative_products'),
      where('slug', '==', slug),
      where('active', '==', true)
    );
    const cooperativeSnap = await getDocs(cooperativeQuery);
    
    if (!cooperativeSnap.empty) {
      const doc = cooperativeSnap.docs[0];
      const data = { id: doc.id, ...doc.data() };
      if (data.deleted !== true) {
        return { ...data, storeType: STORE_TYPES.COOPERATIVE, storeId: 'cooperative' };
      }
    }

    // Search specific cooperative stores
    const cooperativeStoresQuery = query(
      collection(db, 'stores'),
      where('type', '==', STORE_TYPES.COOPERATIVE),
      where('status', '==', 'active')
    );
    const cooperativeStoresSnap = await getDocs(cooperativeStoresQuery);
    
    for (const storeDoc of cooperativeStoresSnap.docs) {
      try {
        const specificCooperativeQuery = query(
          collection(db, `cooperative_products_${storeDoc.id}`),
          where('slug', '==', slug),
          where('active', '==', true)
        );
        const specificCooperativeSnap = await getDocs(specificCooperativeQuery);
        
        if (!specificCooperativeSnap.empty) {
          const doc = specificCooperativeSnap.docs[0];
          const data = { id: doc.id, ...doc.data() };
          if (data.deleted !== true) {
            return { ...data, storeType: STORE_TYPES.COOPERATIVE, storeId: storeDoc.id };
          }
        }
      } catch (error) {
        console.warn(`Error searching cooperative store ${storeDoc.id}:`, error);
      }
    }

    // Search member stores
    const memberStoresQuery = query(
      collection(db, 'stores'),
      where('type', '==', STORE_TYPES.MEMBER),
      where('status', '==', 'active')
    );
    const memberStoresSnap = await getDocs(memberStoresQuery);
    
    for (const storeDoc of memberStoresSnap.docs) {
      try {
        const memberQuery = query(
          collection(db, `member_products_${storeDoc.id}`),
          where('slug', '==', slug),
          where('active', '==', true)
        );
        const memberSnap = await getDocs(memberQuery);
        
        if (!memberSnap.empty) {
          const doc = memberSnap.docs[0];
          const data = { id: doc.id, ...doc.data() };
          if (data.deleted !== true) {
            return { ...data, storeType: STORE_TYPES.MEMBER, storeId: storeDoc.id };
          }
        }
      } catch (error) {
        console.warn(`Error searching member store ${storeDoc.id}:`, error);
      }
    }

    // Search establishment stores
    const establishmentStoresQuery = query(
      collection(db, 'stores'),
      where('type', '==', STORE_TYPES.ESTABLISHMENT),
      where('status', '==', 'active')
    );
    const establishmentStoresSnap = await getDocs(establishmentStoresQuery);
    
    for (const storeDoc of establishmentStoresSnap.docs) {
      try {
        const establishmentQuery = query(
          collection(db, `establishment_products_${storeDoc.id}`),
          where('slug', '==', slug),
          where('active', '==', true)
        );
        const establishmentSnap = await getDocs(establishmentQuery);
        
        if (!establishmentSnap.empty) {
          const doc = establishmentSnap.docs[0];
          const data = { id: doc.id, ...doc.data() };
          if (data.deleted !== true) {
            return { ...data, storeType: STORE_TYPES.ESTABLISHMENT, storeId: storeDoc.id };
          }
        }
      } catch (error) {
        console.warn(`Error searching establishment store ${storeDoc.id}:`, error);
      }
    }

    return null;
  } catch (error) {
    console.error('Error finding product by slug:', error);
    return null;
  }
}

/**
 * Find a product by ID across all store collections
 */
export async function findProductById(productId) {
  if (!productId) return null;

  try {
    // Search cooperative products (generic)
    const cooperativeRef = doc(db, 'cooperative_products', productId);
    const cooperativeSnap = await getDoc(cooperativeRef);
    
    if (cooperativeSnap.exists()) {
      const data = { id: cooperativeSnap.id, ...cooperativeSnap.data() };
      if (data.deleted !== true && data.active !== false) {
        return { ...data, storeType: STORE_TYPES.COOPERATIVE, storeId: 'cooperative' };
      }
    }

    // Search specific cooperative stores
    const cooperativeStoresQuery = query(
      collection(db, 'stores'),
      where('type', '==', STORE_TYPES.COOPERATIVE),
      where('status', '==', 'active')
    );
    const cooperativeStoresSnap = await getDocs(cooperativeStoresQuery);
    
    for (const storeDoc of cooperativeStoresSnap.docs) {
      try {
        const specificCooperativeRef = doc(db, `cooperative_products_${storeDoc.id}`, productId);
        const specificCooperativeSnap = await getDoc(specificCooperativeRef);
        
        if (specificCooperativeSnap.exists()) {
          const data = { id: specificCooperativeSnap.id, ...specificCooperativeSnap.data() };
          if (data.deleted !== true && data.active !== false) {
            return { ...data, storeType: STORE_TYPES.COOPERATIVE, storeId: storeDoc.id };
          }
        }
      } catch (error) {
        console.warn(`Error searching cooperative store ${storeDoc.id}:`, error);
      }
    }

    // Search member stores
    const memberStoresQuery = query(
      collection(db, 'stores'),
      where('type', '==', STORE_TYPES.MEMBER),
      where('status', '==', 'active')
    );
    const memberStoresSnap = await getDocs(memberStoresQuery);
    
    for (const storeDoc of memberStoresSnap.docs) {
      try {
        const memberRef = doc(db, `member_products_${storeDoc.id}`, productId);
        const memberSnap = await getDoc(memberRef);
        
        if (memberSnap.exists()) {
          const data = { id: memberSnap.id, ...memberSnap.data() };
          if (data.deleted !== true && data.active !== false) {
            return { ...data, storeType: STORE_TYPES.MEMBER, storeId: storeDoc.id };
          }
        }
      } catch (error) {
        console.warn(`Error searching member store ${storeDoc.id}:`, error);
      }
    }

    // Search establishment stores
    const establishmentStoresQuery = query(
      collection(db, 'stores'),
      where('type', '==', STORE_TYPES.ESTABLISHMENT),
      where('status', '==', 'active')
    );
    const establishmentStoresSnap = await getDocs(establishmentStoresQuery);
    
    for (const storeDoc of establishmentStoresSnap.docs) {
      try {
        const establishmentRef = doc(db, `establishment_products_${storeDoc.id}`, productId);
        const establishmentSnap = await getDoc(establishmentRef);
        
        if (establishmentSnap.exists()) {
          const data = { id: establishmentSnap.id, ...establishmentSnap.data() };
          if (data.deleted !== true && data.active !== false) {
            return { ...data, storeType: STORE_TYPES.ESTABLISHMENT, storeId: storeDoc.id };
          }
        }
      } catch (error) {
        console.warn(`Error searching establishment store ${storeDoc.id}:`, error);
      }
    }

    return null;
  } catch (error) {
    console.error('Error finding product by ID:', error);
    return null;
  }
}

/**
 * Search products in cooperative_products, cooperative_products_{storeId}, and member_products_{storeId} collections
 */
export async function searchAllStoreProducts(searchTerm, limitCount = 50) {
  if (!searchTerm.trim()) return [];

  const allProducts = [];
  const seenIds = new Set();
  
  try {
    // Get cooperative store ID first
    let cooperativeStoreId = null;
    try {
      const storesQuery = query(
        collection(db, 'stores'),
        where('type', '==', 'cooperative'),
        limit(1)
      );
      const storesSnapshot = await getDocs(storesQuery);
      if (!storesSnapshot.empty) {
        cooperativeStoreId = storesSnapshot.docs[0].id;
      }
    } catch (error) {
      console.error('Error fetching cooperative store ID:', error);
    }

    // 1. Search cooperative_products collection (generic)
    try {
      const cooperativeQuery = query(
        collection(db, 'cooperative_products'),
        where('active', '==', true)
      );
      const cooperativeSnapshot = await getDocs(cooperativeQuery);
      
      let cooperativeProducts = cooperativeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        storeType: STORE_TYPES.COOPERATIVE,
        storeId: null
      })).filter(product => product.deleted !== true);
      
      // Apply search filtering
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const searchSynonyms = {
          'lettuce': ['lettuce', 'lett', 'salad', 'green leafy'],
          'peanut': ['peanut', 'mani', 'groundnut', 'pean'],
          'groundnut': ['groundnut', 'peanut', 'mani', 'pean'],
          'mani': ['mani', 'peanut', 'groundnut', 'pean']
        };
        
        const allSearchTerms = [searchLower];
        if (searchSynonyms[searchLower]) {
          allSearchTerms.push(...searchSynonyms[searchLower]);
        }
        
        cooperativeProducts = cooperativeProducts.filter(product => {
          const productText = [
            product.name,
            product.sku,
            product.description,
            product.searchable,
            ...(product.categories || [])
          ].join(' ').toLowerCase();
          
          return allSearchTerms.some(term => productText.includes(term));
        });
      }
      
      const cooperativeResults = { items: cooperativeProducts };
      
      cooperativeResults.items.forEach(product => {
        if (!seenIds.has(product.id)) {
          allProducts.push({
            ...product,
            storeName: 'Cooperative Store',
            storeType: 'cooperative'
          });
          seenIds.add(product.id);
        }
      });
    } catch (error) {
      console.error('Error searching cooperative_products:', error);
    }

    // 1.5. Search cooperative_products_{storeId} collection (specific store)
    if (cooperativeStoreId) {
      try {
        const specificCooperativeQuery = query(
          collection(db, `cooperative_products_${cooperativeStoreId}`),
          where('active', '==', true)
        );
        const specificCooperativeSnapshot = await getDocs(specificCooperativeQuery);
        
        let specificCooperativeProducts = specificCooperativeSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          storeType: STORE_TYPES.COOPERATIVE,
          storeId: cooperativeStoreId
        })).filter(product => product.deleted !== true);
        
        // Apply search filtering
        if (searchTerm.trim()) {
          const searchLower = searchTerm.toLowerCase();
          const searchSynonyms = {
            'lettuce': ['lettuce', 'lett', 'salad', 'green leafy'],
            'peanut': ['peanut', 'mani', 'groundnut', 'pean'],
            'groundnut': ['groundnut', 'peanut', 'mani', 'pean'],
            'mani': ['mani', 'peanut', 'groundnut', 'pean']
          };
          
          const allSearchTerms = [searchLower];
          if (searchSynonyms[searchLower]) {
            allSearchTerms.push(...searchSynonyms[searchLower]);
          }
          
          specificCooperativeProducts = specificCooperativeProducts.filter(product => {
            const productText = [
              product.name,
              product.sku,
              product.description,
              product.searchable,
              ...(product.categories || [])
            ].join(' ').toLowerCase();
            
            return allSearchTerms.some(term => productText.includes(term));
          });
        }
        
        const specificCooperativeResults = { items: specificCooperativeProducts };
        
        specificCooperativeResults.items.forEach(product => {
          if (!seenIds.has(product.id)) {
            allProducts.push({
              ...product,
              storeName: 'Cooperative Store',
              storeType: 'cooperative'
            });
            seenIds.add(product.id);
          }
        });
      } catch (error) {
        console.error(`Error searching cooperative_products_${cooperativeStoreId}:`, error);
      }
    }
    
    // 2. Search member_products_{storeId} collections
    try {
      const storesRef = collection(db, 'stores');
      const memberStoresQuery = query(storesRef, where('type', '==', 'member'));
      const memberStoresSnapshot = await getDocs(memberStoresQuery);
      
      // Filter active stores manually to handle different status formats
      const activeStores = memberStoresSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.active === true || data.status === 'active' || data.status === 'approved';
      });
      
      for (const storeDoc of activeStores) {
        const store = { id: storeDoc.id, ...storeDoc.data() };
        
        try {
          const memberQuery = query(
            collection(db, `member_products_${store.id}`),
            where('active', '==', true)
          );
          const memberSnapshot = await getDocs(memberQuery);
          
          let memberProducts = memberSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            storeType: STORE_TYPES.MEMBER,
            storeId: store.id
          })).filter(product => product.deleted !== true);
          
          // Apply search filtering
          if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            const searchSynonyms = {
              'lettuce': ['lettuce', 'lett', 'salad', 'green leafy'],
              'peanut': ['peanut', 'mani', 'groundnut', 'pean'],
              'groundnut': ['groundnut', 'peanut', 'mani', 'pean'],
              'mani': ['mani', 'peanut', 'groundnut', 'pean']
            };
            
            const allSearchTerms = [searchLower];
            if (searchSynonyms[searchLower]) {
              allSearchTerms.push(...searchSynonyms[searchLower]);
            }
            
            memberProducts = memberProducts.filter(product => {
              const productText = [
                product.name,
                product.sku,
                product.description,
                product.searchable,
                ...(product.categories || [])
              ].join(' ').toLowerCase();
              
              return allSearchTerms.some(term => productText.includes(term));
            });
          }
          
          const memberResults = { items: memberProducts };
          
          memberResults.items.forEach(product => {
            if (!seenIds.has(product.id)) {
              allProducts.push({
                ...product,
                storeName: store.name,
                storeType: 'member'
              });
              seenIds.add(product.id);
            }
          });
          
        } catch (error) {
          console.error(`Error searching member_products_${store.id}:`, error);
        }
      }
    } catch (error) {
      console.error('Error searching member stores:', error);
    }
    
    return allProducts.slice(0, limitCount);
  } catch (error) {
    console.error('Error in global search:', error);
    return [];
  }
}
